// Typed fetch wrapper for the Hardware Hub API. Every call is same-origin `/api/*`: in dev the
// Vite proxy forwards to the hardware-hub-api (which serves real data via snapshot fallback even
// without live BigQuery). credentials:"include" keeps the Cloudflare Access cookie attached.
//
// Each method's return type is pinned to the FROZEN @busright/data-contract response shape, so the
// FE never re-declares field names — the contract is the single source of truth.

import type {
  AccountAttentionResponse,
  ComponentFlowResponse,
  CrossFlagsResponse,
  CustomerResponse,
  CustomersResponse,
  DatasetRequest,
  DatasetResponse,
  DeviceResponse,
  DistrictsResponse,
  DupSerialsResponse,
  ExploreResponse,
  FinanceResponse,
  FinanceWithMoMResponse,
  GeoHexResponse,
  GeoMetricsResponse,
  GeoStatesResponse,
  HealthResponse,
  LifecycleResponse,
  LifecycleWithReplacementAgeResponse,
  MemoryFactResponse,
  OverviewResponse,
  PipelineHealthResponse,
  RecipientScopedBundleResponse,
  RmaFlowWithDispositionResponse,
  Scope,
  SearchResponse,
  SupplyResponse,
  TelemetryResponse,
} from "@busright/data-contract";

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const res = await fetch(`/api${path}`, { credentials: "include", ...init });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
};

// Serialize a DatasetRequest into the generic /api/dataset query string. The scope flattens to
// `scope` (level) + `id`; nested filters become `f.<key>=<value>` params. This is the single shape
// the whole drill engine speaks — no per-metric endpoints.
const datasetQuery = (req: DatasetRequest): string => {
  const params = new URLSearchParams();
  // Wire params MUST match the server's parseDatasetRequest: metricKey + level (+ id/label,
  // filter.<k>, limit). Keep these names in lockstep with routes/api.ts.
  params.set("metricKey", req.metricKey);
  params.set("level", req.scope.level);
  if (req.scope.id) {
    params.set("id", req.scope.id);
  }
  if (req.scope.label) {
    params.set("label", req.scope.label);
  }
  if (req.filters) {
    for (const [key, value] of Object.entries(req.filters)) {
      params.set(`filter.${key}`, value);
    }
  }
  if (typeof req.limit === "number") {
    params.set("limit", String(req.limit));
  }
  return params.toString();
};

const encodeId = (id: string): string => encodeURIComponent(id);

// Encode just the scope level into the recipient request (the contract's optional ?level= hint).
const recipientLevelParam = (scope: Scope): string =>
  scope.level === "account" ? "?level=account" : "?level=district";

export const apiClient = {
  getOverview: (): Promise<OverviewResponse> => request<OverviewResponse>("/overview"),
  getHealth: (): Promise<HealthResponse> => request<HealthResponse>("/health"),
  getTelemetry: (): Promise<TelemetryResponse> => request<TelemetryResponse>("/telemetry"),
  getSupply: (): Promise<SupplyResponse> => request<SupplyResponse>("/supply"),
  getFinance: (): Promise<FinanceResponse> => request<FinanceResponse>("/finance"),
  // AR × hardware-risk correlation (double-flags + honest RMA verdict).
  getCrossFlags: (): Promise<CrossFlagsResponse> => request<CrossFlagsResponse>("/cross-flags"),
  getDistricts: (): Promise<DistrictsResponse> => request<DistrictsResponse>("/districts"),
  // Geo 3D explorer surfaces.
  getGeoMetrics: (): Promise<GeoMetricsResponse> => request<GeoMetricsResponse>("/geo/metrics"),
  getGeoHex: (metric: string): Promise<GeoHexResponse> =>
    request<GeoHexResponse>(`/geo/hex?metric=${encodeURIComponent(metric)}`),
  getGeoStates: (): Promise<GeoStatesResponse> => request<GeoStatesResponse>("/geo/states"),
  // Customer-360.
  getCustomers: (): Promise<CustomersResponse> => request<CustomersResponse>("/customers"),
  getCustomer: (id: string): Promise<CustomerResponse> =>
    request<CustomerResponse>(`/customer/${encodeURIComponent(id)}`),
  // Lifecycle / device drilldown / pipeline health.
  getLifecycle: (): Promise<LifecycleResponse> => request<LifecycleResponse>("/lifecycle"),
  // /api/lifecycle is served widened server-side (LifecyclePayload + the R5 `replacementAge` block).
  // Same endpoint as getLifecycle; this typing exposes the widened shape without a cast at the page.
  getLifecycleWithReplacementAge: (): Promise<LifecycleWithReplacementAgeResponse> =>
    request<LifecycleWithReplacementAgeResponse>("/lifecycle"),
  getDevice: (serial: string): Promise<DeviceResponse> =>
    request<DeviceResponse>(`/device/${encodeURIComponent(serial)}`),
  getPipelineHealth: (): Promise<PipelineHealthResponse> =>
    request<PipelineHealthResponse>("/pipeline-health"),
  // Allow-listed staging-table browser (Data Sources surface).
  getExplore: (table: string): Promise<ExploreResponse> =>
    request<ExploreResponse>(`/explore?table=${encodeURIComponent(table)}`),
  search: (q: string): Promise<SearchResponse> =>
    request<SearchResponse>(`/search?q=${encodeURIComponent(q)}`),
  // ── Iteration D — the generic drill dataset (the heart of "every number is drillable") ─────────
  // One endpoint backs the entire drill engine: rows behind metric M at scope S. The server maps
  // each MetricKey to a reporting read. Until the endpoint is live this 404/500s and the calling
  // hook surfaces the error so the drawer shows an empty/error state (no fabricated rows).
  getDataset: (req: DatasetRequest): Promise<DatasetResponse> =>
    request<DatasetResponse>(`/dataset?${datasetQuery(req)}`),
  // The reusable District/Recipient scoped bundle (superset of the lean RecipientDetail).
  getRecipientBundle: (id: string, level: Scope): Promise<RecipientScopedBundleResponse> =>
    request<RecipientScopedBundleResponse>(
      `/recipient/${encodeId(id)}${recipientLevelParam(level)}`,
    ),
  // RMA flow (open/aging/resolution + replacement rate + inferred not-returned). Served widened
  // server-side with the R7-R9 `disposition` block (see services/rma-disposition.ts), so the client
  // types the widened response and the page reads it without a cast.
  getRmaFlow: (): Promise<RmaFlowWithDispositionResponse> =>
    request<RmaFlowWithDispositionResponse>("/rma"),
  // Memory used/total/free MB fact (fleet + district + per-device rows).
  getMemory: (): Promise<MemoryFactResponse> => request<MemoryFactResponse>("/memory"),
  // Finance widened with the outstanding-vs-paid MoM block (additive; existing fields retained).
  getFinanceMoM: (): Promise<FinanceWithMoMResponse> => request<FinanceWithMoMResponse>("/finance"),
  // ── Iteration D — WS-B3 reporting-view endpoints (R3/R4, R11, R12) ────────────────────────────
  // Component flow by month × product group (tablet/cradle/harness/sim) — standalone, live-only.
  getComponentFlow: (): Promise<ComponentFlowResponse> =>
    request<ComponentFlowResponse>("/supply/component-flow"),
  // Accounts requiring attention — churn-risk / dormant / pilot worklist.
  getAccountsAttention: (): Promise<AccountAttentionResponse> =>
    request<AccountAttentionResponse>("/accounts/attention"),
  // Esper duplicate/blank serial data-quality rows (100% real, no inference).
  getDupSerials: (): Promise<DupSerialsResponse> =>
    request<DupSerialsResponse>("/data-quality/dup-serials"),
} as const;
